import { IonContent, IonPage } from "@ionic/react";
import { useEffect, useMemo, useState } from "react";
import { Link, useHistory } from "react-router-dom";

import { ActionBar } from "../components/ActionBar";
import { Button } from "../components/Button";
import { Chip, ChipRow } from "../components/Chip";
import { EstimateLegend } from "../components/Estimate";
import { FoodEntrySheet } from "../components/FoodEntrySheet";
import { InsightList } from "../components/Insight";
import { FoodPicker, mealNow } from "../components/FoodPicker";
import { Hero } from "../components/Hero";
import { I } from "../components/icons";
import { MealSection } from "../components/MealSection";
import { Notice } from "../components/Notice";
import { NutrientBars } from "../components/NutrientBars";
import { ProgressBar } from "../components/ProgressBar";
import { DiscloseRow, NavRow, RowGroup } from "../components/Rows";
import { Segmented } from "../components/Segmented";
import { Screen } from "../components/Shell";
import { TopBar } from "../components/TopBar";
import { addDays, dayLabel, parseDay, todayStr } from "../domain/date";
import { againEntry, customFoodByCode } from "../domain/customFoods";
import { isFastDay } from "../domain/dayMarks";
import {
  defaultDeficit,
  defaultProteinPerKg,
  mealProtein,
  mealProteinLine,
  older,
  proteinRule,
} from "../domain/eating";
import { eatingSteps, sleepLine } from "../domain/eatingQuestions";
import { dayKcal } from "../domain/energy";
import { ESTIMATE_LEGEND, isEstimatedEntry, plannedDayCount } from "../domain/foodView";
import { answersFast } from "../domain/planner";
import { mealShares, planRowValue, treatLine } from "../domain/planView";
import {
  MEALS,
  MEAL_LABEL,
  byMeal,
  energyBalance,
  entriesFor,
  fitFactor,
  foodOn,
  frequentFoods,
  isLogged,
  nutrientShare,
  planFor,
  scaleItems,
  totalsOf,
} from "../domain/intake";
import { dailyTargets } from "../domain/nutrition";
import { workParts } from "../domain/today";
import { GLASS_ML, waterRowValue } from "../domain/water";
import type { FoodEntry, MealSlot } from "../domain/types";
import { foodByCode, loadFoods } from "../lib/foodDb";
import { useLogDrink } from "../lib/useLogDrink";
import { useDeviceAnswers, useKaya } from "../store/kayaStore";
import {
  addFoodEntry,
  copyDayFood,
  logPlanned,
  removePlanItem,
  replacePlanItems,
  repeatMeal,
} from "../store/toggles";
import "./Food.css";

/** Yesterday is for the dinner logged the next morning. Tomorrow is not here: planning a day
 *  lives in the plan, which already covers the next seven. */
type Day = "yesterday" | "today";
const DAYS = [
  { value: "yesterday" as const, label: "Yesterday" },
  { value: "today" as const, label: "Today" },
];

const n = (v: number) => v.toLocaleString();

/**
 * The Food tab.
 *
 * Answer first (invariant 9): how much room is left today, which opens the day's energy. Then
 * one-tap chips for what is eaten every day, each meal as a heading over its own group of rows,
 * the plan, and the nutrients folded into a row. Add food is docked at the bottom edge, where a
 * thumb reaches it.
 * With tracking off the tab still answers something — what the day burns — and offers the switch.
 */
export default function Food() {
  const { S, update, showToast, meta } = useKaya();
  const device = useDeviceAnswers();
  const history = useHistory();
  const logDrink = useLogDrink();
  const [day, setDay] = useState<Day>("today");
  const today = todayStr();
  const ds = day === "today" ? today : addDays(today, -1);
  const prevDs = addDays(ds, -1);
  const word = day;

  // `null` is closed; a meal opens the picker on that meal, "now" lets the clock choose.
  const [picking, setPicking] = useState<MealSlot | "now" | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);
  const [ready, setReady] = useState(false);
  const [loadErr, setLoadErr] = useState<string | null>(null);

  useEffect(() => {
    loadFoods()
      .then(() => setReady(true))
      .catch((e) => setLoadErr(e instanceof Error ? e.message : "Could not load the food list"));
  }, []);

  const on = foodOn(S);
  // A fast the person chose carries no deficit (ADR-049); the hero says why the budget is Out.
  const fast = isFastDay(S, ds);
  const entries = entriesFor(S, ds);
  const totals = totalsOf(entries);
  const meals = byMeal(entries);
  const openPlan = planFor(S, ds).filter((e) => !isLogged(S, ds, e.id));
  const planned = byMeal(openPlan);
  // One legend line, and only while a row on screen carries the "~" (SNIPPETS §39).
  const anyEstimated = entries.some(isEstimatedEntry) || openPlan.some(isEstimatedEntry);
  const prevMeals = byMeal(entriesFor(S, prevDs));
  const prevCount = MEALS.reduce((a, m) => a + prevMeals[m].length, 0);

  const measured = Math.round(dayKcal(S, ds));
  const bal = energyBalance(S, ds, measured, parseDay(ds), device);
  const targets = dailyTargets(S, parseDay(ds), measured, device);
  const share = nutrientShare(totals);
  const mealRows = mealProtein(entries, targets.mealProtein);
  const mealLine = mealProteinLine(mealRows, targets.mealProtein);
  const frequent = useMemo(() => frequentFoods(S, 8), [S]);
  const plannedDays = plannedDayCount(S, today);
  const planRow = planRowValue(S, today);
  // Once the day has a plan, each meal's heading reads against its share of the budget
  // (FoodPlannedDay); a day with no plan keeps the plain headings.
  const shares = bal && planFor(S, ds).length ? mealShares(S, ds, bal.budget, answersFast(S, ds)) : {};
  const treat = day === "today" ? treatLine(S, ds, (c) => customFoodByCode(S, c) ?? foodByCode(c)) : null;
  const opened = openId ? (entries.find((e) => e.id === openId) ?? null) : null;
  const prevWord = day === "today" ? "yesterday" : "the day before";

  const add = (e: FoodEntry) => {
    update((d) => addFoodEntry(d, ds, e));
    showToast(`${e.name} · ${n(e.kcal)} kcal`);
  };

  const again = (prev: FoodEntry) => {
    // A food of the user's own resolves through their list first; one since edited or deleted is
    // re-logged from the entry's own copied values (ADR-032).
    const item = prev.code ? (customFoodByCode(S, prev.code) ?? foodByCode(prev.code)) : null;
    const e = againEntry(prev, item, { meal: mealNow() });
    if (!e) {
      showToast("That food is no longer in the list");
      return;
    }
    add(e);
  };

  const fitFor = (m: MealSlot): (() => void) | undefined => {
    const items = planned[m];
    const room = bal ? bal.left : 0;
    const have = items.reduce((a, e) => a + e.kcal, 0);
    if (!bal || !items.length || room <= 0 || have <= room) return undefined;
    const factor = fitFactor(items, room);
    if (factor === null) return undefined;
    return () => {
      update((d) =>
        replacePlanItems(
          d,
          ds,
          items.map((e) => e.id),
          scaleItems(items, factor, foodByCode)
        )
      );
      showToast(`${MEAL_LABEL[m]} scaled to ${n(Math.round(room))} kcal`);
    };
  };

  const turnOn = () => {
    update((d) => {
      d.profile.foodOn = true;
      // The defaults FoodSettings would save, written out so the switch alone gives a budget.
      if (typeof d.profile.deficitKcal !== "number") d.profile.deficitKcal = defaultDeficit(d);
      if (typeof d.profile.proteinPerKg !== "number") d.profile.proteinPerKg = defaultProteinPerKg(d);
    });
    showToast("Food tracking on");
  };

  const toEnergy = () => history.push("/food/energy");
  const work = workParts(S, today).total;

  /* ---------- tracking off: the day's burn, and the switch ---------- */
  if (!on) {
    return (
      <IonPage>
        <IonContent>
          <Screen>
            <div className="k-food">
              <TopBar title="Food" sub={dayLabel(today)} />
              <Hero
                label="Out today, est."
                figure={bal ? `~${n(bal.out)}` : null}
                unit="kcal"
                line={
                  bal
                    ? `at rest ~${n(bal.rest)} · everyday ~${n(bal.baseline)} · work ${work !== null ? `~${n(work)}` : "–"}`
                    : "Add your year of birth and sex to see the day’s burn."
                }
                onPress={toEnergy}
              />
              <Notice tone="info" icon={I.bowl} primary={{ label: "Turn on", onClick: turnOn }}>
                Track what you eat, and Food shows what is left of today&rsquo;s budget.
              </Notice>
              <RowGroup>
                <NavRow
                  icon={I.flame}
                  title="Energy today"
                  subtitle="At rest, everyday and work"
                  value={bal ? `~${n(bal.out)}` : "–"}
                  to="/food/energy"
                />
                <NavRow
                  kind="disabled"
                  icon={I.calendar}
                  title="Plan the week"
                  subtitle="Turn on food tracking to plan meals"
                />
                <NavRow icon={I.bowl} title="Food & calories" value="Tracking is off" to="/you/food" />
              </RowGroup>
            </div>
          </Screen>
        </IonContent>
      </IonPage>
    );
  }

  /* ---------- the answer ---------- */
  const over = bal ? bal.left < 0 : false;
  const hero = bal ? (
    <Hero
      label={over ? `Over ${word}, est.` : `Left ${word}, est.`}
      figure={`~${n(Math.abs(bal.left))}`}
      unit="kcal"
      visual={
        <ProgressBar
          value={bal.in}
          max={bal.budget}
          ceiling
          label={`${n(bal.in)} kcal in`}
          detail={`budget ~${n(bal.budget)}`}
          ariaLabel={`${n(bal.in)} kcal in of a budget of about ${n(bal.budget)}`}
        />
      }
      // The deficit is the one asked for in settings, not one that happened. Over the budget but
      // still under Out is a smaller deficit, not a gain, and the parent's board says so plainly.
      line={
        over && bal.in < bal.out
          ? `~${n(bal.out)} out est. · still ~${n(bal.out - bal.in)} under Out`
          : device.gentle
            ? `~${n(bal.out)} out est. · no deficit on this phone`
            : fast
              ? `~${n(bal.out)} out est. · a fast day, no deficit`
            : `~${n(bal.out)} out est. · ${n(bal.deficit)} deficit planned`
      }
      // The questionnaire's sleep answer is the one lever the budget cannot show (ADR-042).
      sub={day === "today" ? (sleepLine(S) ?? undefined) : undefined}
      {...(day === "today" ? { onPress: toEnergy } : {})}
    />
  ) : (
    <Hero
      label={`In ${word}`}
      figure={entries.length ? totals.kcal : null}
      unit="kcal logged"
      line="No budget without your body numbers."
    />
  );

  return (
    <IonPage>
      <IonContent>
        <Screen>
          <div className="k-food">
            <TopBar title="Food" sub={dayLabel(today)} />
            <Segmented ariaLabel="Which day" value={day} onChange={setDay} options={DAYS} />

            {hero}

            {!bal ? (
              <RowGroup>
                <NavRow
                  icon={I.user}
                  title="Add your body numbers"
                  subtitle="Year of birth and sex give the resting burn a budget rests on"
                  to="/you/body"
                />
              </RowGroup>
            ) : null}

            {bal?.aggressive ? (
              <Notice
                tone="caution"
                primary={{ label: "Change the deficit", onClick: () => history.push("/you/food") }}
              >
                That deficit is over a third of the day&rsquo;s burn. Sustainable is 0.25–0.5 kg a
                week; deeper mostly costs muscle and sleep.
              </Notice>
            ) : null}

            {loadErr ? <Notice tone="danger">{loadErr}</Notice> : null}

            {/* one tap for the things actually eaten every day */}
            {frequent.length ? (
              <ChipRow ariaLabel="Log again">
                {frequent.map((f) => (
                  <Chip key={f.id} icon={I.plus} disabled={!ready} onClick={() => again(f)}>
                    {f.name}, {f.qty} {f.unit}
                  </Chip>
                ))}
              </ChipRow>
            ) : null}

            <section className="k-food-meals" aria-label="Meals">
              {MEALS.map((m) => (
                <MealSection
                  key={m}
                  meal={m}
                  entries={meals[m]}
                  planned={planned[m]}
                  onAdd={ready ? () => setPicking(m) : undefined}
                  onOpen={(e) => setOpenId(e.id)}
                  onLogPlanned={(id) => {
                    update((d) => logPlanned(d, ds, id));
                    showToast("Logged");
                  }}
                  onRemovePlanned={(id) => update((d) => removePlanItem(d, ds, id))}
                  repeatLabel={prevMeals[m].length ? `Repeat ${prevWord}` : undefined}
                  onRepeat={() => update((d) => repeatMeal(d, prevDs, m, ds))}
                  onFit={fitFor(m)}
                  share={shares[m]}
                />
              ))}
              {anyEstimated ? <EstimateLegend>{ESTIMATE_LEGEND}</EstimateLegend> : null}
              <RowGroup>
                {/* Water is intake, so Food owns it; the glass logs without leaving the tab (ADR-044) */}
                <NavRow
                  icon={I.glass}
                  title="Water"
                  value={waterRowValue(S, ds)}
                  to="/food/water"
                  trail={
                    <Button
                      variant="text"
                      icon={I.plus}
                      ariaLabel={`Add a glass of water, ${GLASS_ML} ml`}
                      onClick={() => logDrink(ds, { ml: GLASS_ML, kind: "water", note: "Glass" })}
                    >
                      glass
                    </Button>
                  }
                />
                {/* Until the eating answers exist the plan has nothing to plan from, so the row asks
                    them first (ADR-042); after that it is the week's plan, and the answers are
                    changed from You › Food & calories. */}
                {S.profile.eating?.at === undefined ? (
                  <NavRow
                    icon={I.calendar}
                    title="Plan my food"
                    subtitle="About 3 minutes, all taps"
                    value={meta.eatingDraft ? "Carry on" : `${eatingSteps(S, {}).length} questions`}
                    to="/food/questions"
                  />
                ) : planRow.generated ? (
                  <NavRow icon={I.calendar} title="This week’s plan" value={`${planRow.planned} of 7 days`} to="/food/plan" />
                ) : (
                  <NavRow
                    icon={I.calendar}
                    title="Plan the week"
                    value={plannedDays ? `${plannedDays} day${plannedDays === 1 ? "" : "s"} planned` : "Nothing planned"}
                    to="/food/plan"
                  />
                )}
              </RowGroup>
              {/* The one planned treat of the day, said before the craving it answers (ADR-043) */}
              {treat ? (
                <InsightList insights={[{ key: "plan.treat", text: treat.text, basedOn: treat.basedOn.replace(/^based on /, "") }]} />
              ) : null}
            </section>

            <RowGroup ariaLabel="Nutrients">
              <DiscloseRow
                icon={I.bowl}
                title="Nutrients"
                value={`${Math.round(totals.p)} / ${targets.protein} g protein`}
              >
                <div className="k-food-form">
                  <NutrientBars totals={totals} targets={targets} entries={entries} />
                  {share ? (
                    <p className="k-food-note">
                      Energy from protein {share.p}% · carbs {share.c}% · fat {share.f}%
                    </p>
                  ) : null}
                  {/* After 60 the spread matters as much as the total (ADR-039), so the panel
                      shows each main meal against the per-meal floor. */}
                  {older(S) ? (
                    <div className="k-food-nbars">
                      <p className="k-food-subhead">Protein by meal · aim {targets.mealProtein} g a meal</p>
                      {mealRows.map((r) => (
                        <ProgressBar
                          key={r.meal}
                          value={r.g}
                          max={targets.mealProtein}
                          label={MEAL_LABEL[r.meal]}
                          detail={`${r.g === null ? "–" : r.g} g of ${targets.mealProtein} g${r.short ? " · short" : ""}`}
                          ariaLabel={`${MEAL_LABEL[r.meal]} ${r.g === null ? "not logged" : `${r.g} of ${targets.mealProtein} g protein`}`}
                        />
                      ))}
                      {mealLine ? <p className="k-food-note">{mealLine}</p> : null}
                    </div>
                  ) : null}
                  <p className="k-food-note">
                    Protein is {proteinRule(S).perKg.toFixed(1)} g per kg:{" "}
                    {proteinRule(S).reason === "kidney"
                      ? "held at the adult allowance for kidney disease — your doctor sets this."
                      : proteinRule(S).reason === "glp1"
                        ? "the floor on a GLP-1 or weight-loss medicine, which keeps the muscle."
                        : older(S)
                        ? "the range for 60+, spread across the day."
                        : "the range for training in a deficit, well above the 0.83 g/kg floor."}{" "}
                    <Link to="/you/food">How each target is set</Link>
                  </p>
                </div>
              </DiscloseRow>
            </RowGroup>

            <p className="k-food-note">
              Calorie figures for home food vary by roughly a third with oil and portion alone. Log
              consistently and watch the trend; do not trust any single day to the calorie.
            </p>
          </div>

          <ActionBar
            primary={{
              label: "Add food",
              icon: I.plus,
              onClick: () => setPicking("now"),
              disabled: !ready,
              reason: ready ? undefined : loadErr ? "The food list did not load" : "Loading the food list",
            }}
            secondary={
              !entries.length && prevCount
                ? {
                    label: `Copy ${prevWord}`,
                    onClick: () => {
                      update((d) => copyDayFood(d, prevDs, ds));
                      showToast(`Copied ${prevCount} item${prevCount === 1 ? "" : "s"}`);
                    },
                  }
                : undefined
            }
          />
        </Screen>

        {picking ? (
          <FoodPicker
            title={day === "today" ? "Add food" : "Add food for yesterday"}
            meal={picking === "now" ? undefined : picking}
            quick={frequent}
            onClose={() => setPicking(null)}
            onPick={(e) => {
              add(e);
              setPicking(null);
            }}
            onPickMany={(es) => {
              update((d) => {
                for (const e of es) addFoodEntry(d, ds, e);
              });
              showToast(`${es.length} food${es.length === 1 ? "" : "s"} · ${n(es.reduce((a, e) => a + e.kcal, 0))} kcal`);
              setPicking(null);
            }}
          />
        ) : null}

        {opened ? <FoodEntrySheet entry={opened} ds={ds} onClose={() => setOpenId(null)} /> : null}
      </IonContent>
    </IonPage>
  );
}
